import { css } from 'styled-components';
export const bpDefaults = {
    xxxs: 0,
    xxs: 200,
    xs: 320,
    s: 400,
    sl: 500,
    m: 768,
    ml: 992,
    l: 1100,
    xl: 1200,
    xxl: 1300,
    xxxl: 1400,
    xxxxl: 1500,
    xxxxxl: 1600,
    xxxxxxl: 1700,
};
export const getBpValue = (val, bp) => {
    switch (typeof val) {
        case 'number':
            return val;
        case 'string':
            if (!bp.hasOwnProperty(val)) {
                throw new Error(`Media Query error: oops, you passed a value that is not defined in the 'bp' object.`);
            }
            if (typeof bp[val] !== 'number') {
                throw new Error(`Media Query error: oops, you passed a value that is not a proper number.`);
            }
            return bp[val];
        default:
            return -1;
    }
};
/**
 * Initialize media query
 *
 * ### Usage
 * ```js
 * import { styled, css } from 'styled-components'
 * import MqInit from 'styled-components-media-query'
 *
 * // initialize
 * const bp = {s: 400, sl: 500, m: 768, ml: 992, l: 1100}
 * const mq = MqInit({ bp })
 *
 * // use like this
 * const ComponentStyled = styled.div`
 *  position: relative;
 *  ${mq('m')(css`
 *    position: absolute;
 *  `)}
 *  ${mq('l')(css`
 *    position: fixed;
 *  `)}
 * `
 * ```
 *
 * @param bp      Pass a breakpoint object to customize the breakpoints you wish to use.
 * @param type    Pass a type string to either use min/max-width or min/max-height.
 * @returns       A styled-component css string.
 */
const MqInit = ({ bp = bpDefaults, type = 'width' } = {}) => (min, max) => (contentCSS) => {
    if (!Array.isArray(contentCSS) && typeof contentCSS !== 'string') {
        throw new Error(`Media Query error: oops, you passed a invalid argument. Valid arguments are 'string' or the styled components 'css' helper.`);
    }
    const minV = getBpValue(min, bp);
    const maxV = getBpValue(max, bp);
    const content = Array.isArray(contentCSS) ? contentCSS : css `${contentCSS}`;
    if (minV >= 0 && maxV === -1) {
        return css `
      @media only screen and (min-${type}: ${minV}px) {
        ${content}
      }
    `;
    }
    else if (minV === -1 && maxV >= 0) {
        return css `
      @media only screen and (max-${type}: ${maxV - 1}px) {
        ${content}
      }
    `;
    }
    else if (minV >= 0 && maxV >= 0) {
        return css `
      @media only screen and (min-${type}: ${minV}px) and (max-${type}: ${maxV -
            1}px) {
        ${content}
      }
    `;
    }
    return css ``;
};
export const mqExec = MqInit();
export default MqInit;
//# sourceMappingURL=data:application/json;base64,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